import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Wcash = ({ currentMember, isAdmin }) => {
  const [wallet, setWallet] = useState(null);
  const [wcashBalance, setWcashBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hideBalance, setHideBalance] = useState(false);
  const [cashbackHistory, setCashbackHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showAddCashback, setShowAddCashback] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [cashbackAmount, setCashbackAmount] = useState('');
  const [cashbackDesc, setCashbackDesc] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDesc, setPaymentDesc] = useState('');
  const [sending, setSending] = useState(false);
  const [notif, setNotif] = useState(null);
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpLoading, setTopUpLoading] = useState(null); // lookup_key being processed

  const TOPUP_PACKAGES = [
    { key: 'wcash_topup_100',  amount: 100 },
    { key: 'wcash_topup_500',  amount: 500 },
    { key: 'wcash_topup_1000', amount: 1000 },
    { key: 'wcash_topup_5000', amount: 5000 },
  ];

  const startStripeTopUp = async (lookupKey) => {
    if (!currentMember?.id) return;
    setTopUpLoading(lookupKey);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
        member_id: currentMember.id,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setTopUpLoading(null);
        showNotifMsg('Falha ao iniciar checkout', 'error');
      }
    } catch (e) {
      setTopUpLoading(null);
      showNotifMsg(e?.response?.data?.detail || 'Erro no checkout', 'error');
    }
  };

  const showNotifMsg = (msg, type = 'info') => {
    setNotif({ msg, type });
    setTimeout(() => setNotif(null), 3000);
  };

  const loadWallet = useCallback(async () => {
    if (!currentMember) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wallet/${currentMember.id}`);
      setWallet(res.data);
    } catch (e) {
      if (e.response?.status === 404) {
        // Auto-create wallet if not exists
        try {
          const createRes = await axios.post(`${API}/wallet/create`, { member_id: currentMember.id });
          setWallet(createRes.data);
        } catch (createErr) {
          console.error('Error creating wallet:', createErr);
          setWallet(null);
        }
      } else {
        console.error('Error loading wallet:', e);
        setWallet(null);
      }
    } finally {
      setLoading(false);
    }
  }, [currentMember]);

  const loadWcashBalance = useCallback(async () => {
    if (!currentMember || !wallet) return;
    try {
      const res = await axios.get(`${API}/wallet/${currentMember.id}/wcash`);
      setWcashBalance(res.data?.wcash_balance || 0);
    } catch (e) {
      console.error('Error loading WCash balance:', e);
      setWcashBalance(0);
    }
  }, [currentMember, wallet]);

  const loadCashbackHistory = useCallback(async () => {
    if (!currentMember) return;
    try {
      const res = await axios.get(`${API}/wallet/${currentMember.id}/cashback`);
      setCashbackHistory(res.data || []);
    } catch (e) {
      setCashbackHistory([]);
    }
  }, [currentMember]);

  useEffect(() => {
    loadWallet();
    loadMembers();
  }, [loadWallet]);

  const loadMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/members`);
      setMembers(res.data || []);
    } catch (e) {
      setMembers([]);
    }
  }, []);

  useEffect(() => {
    if (wallet) {
      loadWcashBalance();
      loadCashbackHistory();
      const interval = setInterval(() => {
        loadWcashBalance();
        loadCashbackHistory();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [wallet, loadWcashBalance, loadCashbackHistory]);

  // Parse amount handling Brazilian format (5.000 = 5000, 5,50 = 5.50)
  const parseAmount = (value) => {
    if (!value) return 0;
    // Remove dots as thousand separators, replace comma with dot for decimals
    const cleaned = value.toString().replace(/\./g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  };

  const addCashback = async () => {
    const amount = parseAmount(cashbackAmount);
    if (!selectedMember || !cashbackAmount || amount <= 0) {
      showNotifMsg('Selecione membro e valor', 'error');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/wallet/${selectedMember.id}/cashback`, {
        amount: amount,
        description: cashbackDesc || 'Cashback Winners Club',
        source: 'Admin'
      });
      showNotifMsg(`R$ ${amount.toLocaleString('pt-BR')} de cashback adicionado!`, 'success');
      setShowAddCashback(false);
      setSelectedMember(null);
      setCashbackAmount('');
      setCashbackDesc('');
      if (selectedMember.id === currentMember?.id) {
        loadWcashBalance();
        loadCashbackHistory();
      }
    } catch (e) {
      showNotifMsg(e.response?.data?.detail || 'Erro ao adicionar', 'error');
    } finally {
      setSending(false);
    }
  };

  const useWcashPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      showNotifMsg('Digite um valor', 'error');
      return;
    }
    if (parseFloat(paymentAmount) > wcashBalance) {
      showNotifMsg('Saldo insuficiente', 'error');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/wallet/${currentMember.id}/wcash/use`, {
        amount: parseFloat(paymentAmount),
        description: paymentDesc || 'Pagamento com WCash'
      });
      showNotifMsg(`R$ ${paymentAmount} utilizado!`, 'success');
      setShowPayment(false);
      setPaymentAmount('');
      setPaymentDesc('');
      loadWcashBalance();
      loadCashbackHistory();
    } catch (e) {
      showNotifMsg(e.response?.data?.detail || 'Erro ao usar WCash', 'error');
    } finally {
      setSending(false);
    }
  };

  const formatNumber = (num, decimals = 2) => {
    if (!num || isNaN(num)) return '0,00';
    return Number(num).toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  };

  // No member selected
  if (!currentMember) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
        <div className="w-20 h-20 bg-white/[0.03] rounded-full flex items-center justify-center mb-4">
          <svg className="w-10 h-10 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-white/40 font-['Jost']">Selecione um membro na aba Cartoes</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-black" data-testid="wcash-section">
      {/* Liquid Glass Background Effects - Blue/Gray/Silver palette */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 -left-20 w-72 h-72 bg-blue-500/10 rounded-full blur-[100px]" />
        <div className="absolute top-40 -right-20 w-72 h-72 bg-slate-400/8 rounded-full blur-[100px]" />
        <div className="absolute bottom-40 left-1/3 w-60 h-60 bg-blue-600/8 rounded-full blur-[80px]" />
      </div>

      {/* Header with Liquid Glass - Blue/Silver theme */}
      <div className="relative">
        <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-blue-900/20 via-slate-900/10 to-transparent" />
        
        <div className="relative px-4 pt-4 pb-2">
          {/* Top bar - Logo W transparente + CASH */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <img src="/w-logo.png" alt="W" className="w-12 h-12 object-contain" />
            <span className="text-white/90 font-['Jost'] text-xl tracking-wider">CASH</span>
          </div>

          {/* Cashback Balance Card - Liquid Glass with Animated Border */}
          <div className="relative rounded-2xl p-5 mb-6 backdrop-blur-xl overflow-hidden"
               style={{ background: 'linear-gradient(145deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 50%, rgba(255,255,255,0.04) 100%)' }}>
            
            {/* Animated border light effect - Blue/Silver gradient */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 400 130" preserveAspectRatio="none">
              <defs>
                <linearGradient id="wcashLightGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                  <stop offset="40%" stopColor="rgba(59,130,246,0.6)" />
                  <stop offset="50%" stopColor="rgba(203,213,225,0.9)" />
                  <stop offset="60%" stopColor="rgba(100,116,139,0.6)" />
                  <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                </linearGradient>
              </defs>
              <rect
                x="1" y="1" width="398" height="128" rx="16" ry="16"
                fill="none"
                stroke="url(#wcashLightGrad)"
                strokeWidth="1.5"
                strokeDasharray="1200"
                className="wcash-border-light"
              />
            </svg>
            <style>{`
              @keyframes wcashBorderLight {
                0% { stroke-dashoffset: 0; }
                100% { stroke-dashoffset: -2400; }
              }
              .wcash-border-light {
                animation: wcashBorderLight 10s linear infinite;
              }
            `}</style>

            <div className="relative z-10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/50 text-xs font-['Jost'] tracking-wider">SALDO DE CASHBACK</span>
                <button onClick={() => setHideBalance(!hideBalance)} className="text-white/40">
                  {hideBalance ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              
              {loading ? (
                <div className="w-8 h-8 border-2 border-white/10 border-t-white/40 rounded-full animate-spin mx-auto" />
              ) : (
                <h1 className="text-4xl font-light text-white tracking-tight">
                  {hideBalance ? '••••••' : `R$ ${formatNumber(wcashBalance)}`}
                </h1>
              )}
              
              <p className="text-white/30 text-xs mt-2">
                = {hideBalance ? '••••' : formatNumber(wcashBalance)} WCash (1:1 BRL)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons - Liquid Glass Blue/Silver theme */}
      {wallet && (
        <div className="px-4 mb-6">
          <div className="grid grid-cols-2 gap-3">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowPayment(true)}
              className="flex flex-col items-center gap-2 py-4 rounded-2xl border border-blue-500/20 transition backdrop-blur-xl hover:border-blue-500/30"
              style={{ 
                background: 'linear-gradient(145deg, rgba(59,130,246,0.1) 0%, rgba(59,130,246,0.05) 100%)',
                boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.05)'
              }}
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-500/20">
                <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <span className="text-xs font-['Jost'] text-blue-400">Pagar com Wcash</span>
            </motion.button>
            
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowHistory(true)}
              className="flex flex-col items-center gap-2 py-4 rounded-2xl border border-white/[0.08] transition backdrop-blur-xl hover:border-white/[0.15]"
              style={{ 
                background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.05)'
              }}
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-500/20">
                <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <span className="text-xs font-['Jost'] text-white/60">Histórico</span>
            </motion.button>
          </div>
          
          {/* Recarregar via Stripe — todos os membros */}
          <button
            onClick={() => setShowTopUp(true)}
            data-testid="wcash-topup-btn"
            className="w-full mt-3 py-3 rounded-xl flex items-center justify-center gap-2 border border-emerald-500/20 transition backdrop-blur-xl hover:border-emerald-500/40"
            style={{
              background: 'linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(6,95,70,0.05) 100%)',
              boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.05)'
            }}
          >
            <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m0-16l-4 4m4-4l4 4M4 20h16" />
            </svg>
            <span className="text-sm font-['Jost'] text-emerald-400">Recarregar Wcash</span>
          </button>

          {/* Admin: Add Cashback Button */}
          {isAdmin && (
            <button
              onClick={() => setShowAddCashback(true)}
              className="w-full mt-3 py-3 rounded-xl flex items-center justify-center gap-2 border border-blue-500/20 transition backdrop-blur-xl hover:border-blue-500/30"
              style={{ 
                background: 'linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(100,116,139,0.05) 100%)',
                boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.05)'
              }}
            >
              <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="text-sm font-['Jost'] text-blue-400">Adicionar Cashback (Admin)</span>
            </button>
          )}
        </div>
      )}

      {/* Info Section */}
      <div className="px-4 mb-6">
        <p className="text-white/40 text-xs font-['Jost'] tracking-wider mb-3">SOBRE O WCASH</p>
        <div className="bg-white/[0.03] rounded-2xl border border-white/[0.06] p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img src="/w-logo.png" alt="W" className="w-8 h-8 object-contain" />
            </div>
            <div>
              <p className="text-white/80 font-['Jost'] text-sm mb-1">Cashback WClub</p>
              <p className="text-white/40 text-xs leading-relaxed">
                O Wcash é o sistema de cashback exclusivo do WClub. Cada Wcash vale R$ 1,00 e pode ser usado para pagamentos de eventos, mensalidades e benefícios no clube.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="px-4 mb-6">
        <p className="text-white/40 text-xs font-['Jost'] tracking-wider mb-3">COMO FUNCIONA</p>
        <div className="bg-white/[0.03] rounded-2xl border border-white/[0.06] p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-blue-400 text-xs font-bold">1</span>
            </div>
            <p className="text-white/60 text-xs">Ganhe Wcash em compras e eventos do WClub</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-blue-400 text-xs font-bold">2</span>
            </div>
            <p className="text-white/60 text-xs">Use seu saldo para pagar eventos e mensalidades</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-blue-400 text-xs font-bold">3</span>
            </div>
            <p className="text-white/60 text-xs">O valor é creditado diretamente na conta do WClub</p>
          </div>
        </div>
      </div>

      {/* History Button */}
      <div className="px-4 mb-6">
        <button
          onClick={() => setShowHistory(true)}
          className="w-full bg-white/[0.03] rounded-2xl border border-white/[0.06] p-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/[0.04] rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="text-white/80 font-['Jost']">Histórico de Wcash</span>
          </div>
          <svg className="w-5 h-5 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Recent Cashback */}
      <div className="px-4 flex-1">
        <p className="text-white/40 text-xs font-['Jost'] tracking-wider mb-3">CASHBACK RECENTE</p>
        
        <div className="space-y-3 pb-8">
          {cashbackHistory.length === 0 ? (
            <div className="text-center py-8 bg-white/[0.02] rounded-2xl">
              <div className="w-12 h-12 bg-white/[0.03] rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                </svg>
              </div>
              <p className="text-white/30 text-sm font-['Jost']">Nenhum cashback ainda</p>
              <p className="text-white/20 text-xs mt-1">Faça compras para ganhar WCash!</p>
            </div>
          ) : (
            cashbackHistory.slice(0, 5).map((item, i) => {
              const isExpense = item.type === 'usage' || item.amount < 0;
              const displayAmount = Math.abs(item.amount);
              
              return (
                <motion.div
                  key={item.id || i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-white/[0.02] rounded-xl p-3 flex items-center gap-3"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isExpense ? 'bg-white/5' : 'bg-emerald-500/10'}`}>
                    {isExpense ? (
                      <svg className="w-4 h-4 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-white/80 text-sm font-['Jost']">{item.description || 'Cashback'}</p>
                    <p className="text-white/40 text-xs">{item.source || 'Winners Club'}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-['Jost'] ${isExpense ? 'text-white/60' : 'text-emerald-400'}`}>
                      {isExpense ? '- ' : '+ '}R$ {formatNumber(displayAmount)}
                    </p>
                    <p className="text-white/30 text-[10px]">{formatDate(item.created_at)}</p>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
      </div>

      {/* Notification - Blue/Gray theme */}
      <AnimatePresence>
        {notif && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className={`fixed bottom-20 left-4 right-4 px-4 py-3 rounded-xl text-sm font-['Jost'] text-center ${
              notif.type === 'success' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
              notif.type === 'error' ? 'bg-slate-500/20 text-slate-300 border border-slate-500/30' :
              'bg-white/10 text-white/80 border border-white/20'
            }`}
          >
            {notif.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* History Modal */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end justify-center"
            onClick={() => setShowHistory(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="w-full max-w-md bg-black/95 rounded-t-3xl p-6 max-h-[80vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="w-12 h-1 bg-white/20 rounded-full mx-auto mb-6" />
              <h3 className="text-lg font-['Jost'] text-white/90 text-center mb-6">Histórico de Cashback</h3>
              
              <div className="space-y-3">
                {cashbackHistory.length === 0 ? (
                  <p className="text-white/40 text-center py-8">Nenhum cashback registrado</p>
                ) : (
                  cashbackHistory.map((item, i) => {
                    const isExpense = item.type === 'usage' || item.amount < 0;
                    const displayAmount = Math.abs(item.amount);
                    
                    return (
                      <div
                        key={item.id || i}
                        className="bg-white/[0.03] rounded-xl p-3 flex items-center gap-3 border border-white/[0.06]"
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isExpense ? 'bg-white/5' : 'bg-emerald-500/10'}`}>
                          {isExpense ? (
                            <svg className="w-4 h-4 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-white/80 text-sm font-['Jost']">{item.description || 'Cashback'}</p>
                          <p className="text-white/40 text-xs">{item.source || 'Winners Club'}</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-['Jost'] ${isExpense ? 'text-white/60' : 'text-emerald-400'}`}>
                            {isExpense ? '- ' : '+ '}R$ {formatNumber(displayAmount)}
                          </p>
                          <p className="text-white/30 text-[10px]">{formatDate(item.created_at)}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Use Wcash Payment Modal */}
      <AnimatePresence>
        {showPayment && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end justify-center"
            onClick={() => setShowPayment(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="w-full max-w-md bg-black/95 rounded-t-3xl p-6"
              onClick={e => e.stopPropagation()}
            >
              <div className="w-12 h-1 bg-white/20 rounded-full mx-auto mb-6" />
              <h3 className="text-lg font-['Jost'] text-white/90 text-center mb-2">Pagar com Wcash</h3>
              <p className="text-white/40 text-xs text-center mb-6">O valor sera creditado na conta do WClub</p>
              
              {/* Amount Input */}
              <div className="mb-4">
                <label className="text-[10px] text-white/40 uppercase tracking-wider mb-2 block">Valor do Pagamento</label>
                <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] flex items-center px-4">
                  <span className="text-white/40 mr-2">R$</span>
                  <input
                    type="number"
                    value={paymentAmount}
                    onChange={e => setPaymentAmount(e.target.value)}
                    placeholder="0,00"
                    className="flex-1 py-4 bg-transparent text-white text-xl font-['Jost'] outline-none"
                  />
                </div>
                <p className="text-white/30 text-xs mt-2">Saldo disponivel: R$ {formatNumber(wcashBalance)}</p>
              </div>

              {/* Description Input */}
              <div className="mb-4">
                <label className="text-[10px] text-white/40 uppercase tracking-wider mb-2 block">Descricao</label>
                <input
                  type="text"
                  value={paymentDesc}
                  onChange={e => setPaymentDesc(e.target.value)}
                  placeholder="Ex: Evento Degustacao, Mensalidade Jan/25"
                  className="w-full py-3 px-4 bg-white/[0.03] rounded-xl border border-white/[0.06] text-white text-sm outline-none placeholder:text-white/20"
                />
              </div>

              {/* Info about where payment goes */}
              <div className="mb-6 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <div className="flex items-center gap-2">
                  <img src="/w-logo.png" alt="W" className="w-5 h-5" />
                  <p className="text-blue-400 text-xs">Pagamento para conta WClub</p>
                </div>
              </div>

              {/* Use Button */}
              <button
                onClick={useWcashPayment}
                disabled={!paymentAmount || sending}
                className={`w-full py-4 rounded-xl font-['Jost'] transition ${
                  paymentAmount
                    ? 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400'
                    : 'bg-white/5 text-white/30 cursor-not-allowed'
                }`}
              >
                {sending ? 'Processando...' : `Pagar R$ ${paymentAmount || '0'}`}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Wcash Top-Up Modal (Stripe) */}
      <AnimatePresence>
        {showTopUp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end justify-center"
            onClick={() => setShowTopUp(false)}
            data-testid="wcash-topup-modal"
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              onClick={e => e.stopPropagation()}
              className="w-full max-w-md bg-[#0b0b0e]/95 backdrop-blur-2xl rounded-t-3xl border-t border-white/10 p-6 pb-10"
            >
              <div className="w-12 h-1 bg-white/20 rounded-full mx-auto mb-5" />
              <h3 className="text-lg font-['Jost'] text-white/90 text-center mb-1">Recarregar Wcash</h3>
              <p className="text-white/40 text-xs text-center mb-6">Pagamento seguro processado pelo Stripe</p>

              <div className="grid grid-cols-2 gap-3 mb-4">
                {TOPUP_PACKAGES.map(pkg => (
                  <button
                    key={pkg.key}
                    disabled={!!topUpLoading}
                    onClick={() => startStripeTopUp(pkg.key)}
                    data-testid={`topup-pkg-${pkg.amount}`}
                    className="relative py-5 rounded-2xl border border-white/10 backdrop-blur-xl hover:border-emerald-400/40 transition disabled:opacity-40"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16,185,129,0.06) 0%, rgba(255,255,255,0.02) 100%)',
                    }}
                  >
                    <div className="text-[10px] font-['Jost'] text-emerald-400/80 tracking-widest mb-1">RECARGA</div>
                    <div className="text-2xl font-light text-white">
                      R$ {pkg.amount.toLocaleString('pt-BR')}
                    </div>
                    {topUpLoading === pkg.key && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-2xl">
                        <div className="w-6 h-6 border-2 border-emerald-400/40 border-t-emerald-400 rounded-full animate-spin" />
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <p className="text-white/30 text-[10px] text-center leading-relaxed">
                Você será redirecionado para o Stripe Checkout. O crédito aparece na sua carteira em segundos.
              </p>

              <button
                onClick={() => setShowTopUp(false)}
                className="w-full mt-5 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 text-sm font-['Jost'] transition"
              >
                Cancelar
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Admin Add Cashback Modal */}
      <AnimatePresence>
        {showAddCashback && isAdmin && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end justify-center"
            onClick={() => setShowAddCashback(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="w-full max-w-md bg-[#1a1a20] rounded-t-3xl p-6 max-h-[85vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="w-12 h-1 bg-white/20 rounded-full mx-auto mb-6" />
              <h3 className="text-lg font-['Jost'] text-white/90 text-center mb-2">Adicionar Cashback</h3>
              <p className="text-white/40 text-xs text-center mb-6">Adicione WCash ao saldo de um membro</p>
              
              {/* Amount Input */}
              <div className="mb-4">
                <label className="text-[10px] text-white/40 uppercase tracking-wider mb-2 block">Valor em R$</label>
                <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] flex items-center px-4">
                  <span className="text-white/40 mr-2">R$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={cashbackAmount}
                    onChange={e => setCashbackAmount(e.target.value)}
                    placeholder="5000 ou 5.000"
                    className="flex-1 py-4 bg-transparent text-white text-xl font-['Jost'] outline-none"
                  />
                </div>
                <p className="text-white/30 text-[10px] mt-1">Use ponto como separador de milhar (5.000 = cinco mil)</p>
              </div>

              {/* Description Input */}
              <div className="mb-4">
                <label className="text-[10px] text-white/40 uppercase tracking-wider mb-2 block">Descrição</label>
                <input
                  type="text"
                  value={cashbackDesc}
                  onChange={e => setCashbackDesc(e.target.value)}
                  placeholder="Ex: Cashback compra evento"
                  className="w-full py-3 px-4 bg-white/[0.03] rounded-xl border border-white/[0.06] text-white text-sm outline-none placeholder:text-white/20"
                />
              </div>

              {/* Member Selection */}
              <div className="mb-6">
                <label className="text-[10px] text-white/40 uppercase tracking-wider mb-2 block">Selecionar Membro</label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {members.map(member => (
                    <button
                      key={member.id}
                      onClick={() => setSelectedMember(member)}
                      className={`w-full p-3 rounded-xl flex items-center gap-3 transition ${
                        selectedMember?.id === member.id 
                          ? 'bg-amber-500/20 border border-amber-500/30' 
                          : 'bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06]'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                        <span className="text-white/60 text-sm font-bold">
                          {member.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="text-left flex-1">
                        <p className="text-white/80 text-sm font-['Jost']">{member.name}</p>
                        <p className="text-white/40 text-xs capitalize">{member.cat}</p>
                      </div>
                      {selectedMember?.id === member.id && (
                        <svg className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Add Button */}
              <button
                onClick={addCashback}
                disabled={!selectedMember || !cashbackAmount || sending}
                className={`w-full py-4 rounded-xl font-['Jost'] transition ${
                  selectedMember && cashbackAmount
                    ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-400'
                    : 'bg-white/5 text-white/30 cursor-not-allowed'
                }`}
              >
                {sending ? 'Adicionando...' : `Adicionar R$ ${cashbackAmount || '0'}`}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Wcash;
